import React, { useState, useEffect } from "react";
import { Clock, Globe, Calendar as CalendarIcon, MapPin } from "lucide-react";

const SL_HOLIDAYS_2026 = [
    { date: "2026-01-01", name: "Duruthu Full Moon Poya Day" },
    { date: "2026-01-14", name: "Tamil Thai Pongal Day" },
    { date: "2026-02-04", name: "Independence Day" },
    { date: "2026-02-17", name: "Maha Shivaratri Day" },
    { date: "2026-04-13", name: "Sinhala & Tamil New Year Eve" },
    { date: "2026-04-14", name: "Sinhala & Tamil New Year Day" },
    { date: "2026-05-01", name: "May Day / Vesak Full Moon Poya Day" },
    { date: "2026-05-31", name: "Poson Full Moon Poya Day" },
    { date: "2026-12-25", name: "Christmas Day" },
];

const WorldClock = ({ city, timezone }) => {
    const [time, setTime] = useState(new Date());

    useEffect(() => {
        const timer = setInterval(() => setTime(new Date()), 1000);
        return () => clearInterval(timer);
    }, []);

    const formattedTime = time.toLocaleTimeString("en-GB", {
        timeZone: timezone,
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
    });

    return (
        <div className="bg-white p-4 rounded-xl shadow-sm border border-sky-100 flex flex-col items-center justify-center space-y-2">
            <span className="text-sky-600 font-semibold text-sm uppercase tracking-wider">{city}</span>
            <span className="text-2xl font-mono text-gray-800">{formattedTime}</span>
        </div>
    );
};

const WeatherWidget = () => {
    const [weatherData, setWeatherData] = useState(null);
    const [isLoadingWeather, setIsLoadingWeather] = useState(true);

    useEffect(() => {
        const fetchWeather = async () => {
            try {
                setIsLoadingWeather(true);
                const API_KEY = process.env.REACT_APP_WEATHER_API_KEY;
                const response = await fetch(
                    `https://api.openweathermap.org/data/2.5/weather?q=Colombo,lk&units=metric&appid=${API_KEY}`
                );
                const data = await response.json();

                if (response.ok) {
                    setWeatherData({
                        temp: Math.round(data.main.temp),
                        icon: `https://openweathermap.org/img/wn/${data.weather[0].icon}@2x.png`,
                        description: data.weather[0].description,
                        humidity: data.main.humidity
                    });
                }
            } catch (error) {
                console.error("Failed to fetch weather data:", error);
            } finally {
                setIsLoadingWeather(false);
            }
        };

        fetchWeather();
        const intervalId = setInterval(fetchWeather, 30 * 60 * 1000);
        return () => clearInterval(intervalId);
    }, []);

    return (
        <div className="bg-gradient-to-br from-sky-400 to-sky-600 p-4 rounded-xl shadow-sm flex flex-col items-center justify-center space-y-1 relative overflow-hidden group">
            <div className="absolute -right-8 -top-8 bg-white/10 w-24 h-24 rounded-full group-hover:scale-110 transition duration-500 z-0"></div>
            {isLoadingWeather ? (
                <div className="w-full h-full flex items-center justify-center z-10">
                    <div className="w-8 h-8 rounded-full border-2 border-white/30 border-t-white animate-spin"></div>
                </div>
            ) : weatherData ? (
                <div className="flex flex-col items-center z-10 text-white w-full">
                    <div className="flex items-center justify-between w-full px-2">
                        <span className="font-semibold text-xs uppercase tracking-wider opacity-90">Colombo, LK</span>
                        <span className="text-xs opacity-80 capitalize">{weatherData.description}</span>
                    </div>
                    <div className="flex items-center justify-center gap-2 mt-2">
                        <img src={weatherData.icon} alt="Weather" className="w-12 h-12 drop-shadow-md" />
                        <span className="text-3xl font-bold tracking-tighter">{weatherData.temp}°C</span>
                    </div>
                </div>
            ) : (
                <span className="text-white/80 text-sm z-10">Weather Unavailable</span>
            )}
        </div>
    );
};

const CustomCalendar = () => {
    const [currentDate, setCurrentDate] = useState(new Date());

    const daysInMonth = (month, year) => new Date(year, month + 1, 0).getDate();
    const firstDayOfMonth = (month, year) => new Date(year, month, 1).getDay();

    const monthNames = [
        "January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December"
    ];

    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    const days = [];
    const startDay = firstDayOfMonth(month, year);
    const totalDays = daysInMonth(month, year);

    // Padding for start of month
    for (let i = 0; i < startDay; i++) days.push(null);
    for (let i = 1; i <= totalDays; i++) days.push(i);

    const isHoliday = (d) => {
        if (!d) return null;
        const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
        return SL_HOLIDAYS_2026.find(h => h.date === dateStr);
    };

    const isToday = (d) => {
        const today = new Date();
        return d && d === today.getDate() && month === today.getMonth() && year === today.getFullYear();
    };

    return (
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-sky-100">
            <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                    <CalendarIcon className="text-sky-600" size={24} />
                    {monthNames[month]} {year}
                </h3>
                <div className="flex gap-2">
                    <button
                        onClick={() => setCurrentDate(new Date(year, month - 1))}
                        className="p-2 hover:bg-sky-50 rounded-full text-sky-600 transition"
                    >
                        ◀
                    </button>
                    <button
                        onClick={() => setCurrentDate(new Date(year, month + 1))}
                        className="p-2 hover:bg-sky-50 rounded-full text-sky-600 transition"
                    >
                        ▶
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-7 gap-2 mb-2">
                {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(d => (
                    <div key={d} className="text-center text-xs font-bold text-gray-400 uppercase">{d}</div>
                ))}
            </div>

            <div className="grid grid-cols-7 gap-2">
                {days.map((day, i) => {
                    const holiday = isHoliday(day);
                    return (
                        <div
                            key={i}
                            className={`
                h-14 rounded-lg flex flex-col items-center justify-center relative group cursor-pointer
                ${!day ? "" : "hover:bg-sky-50 transition"}
                ${isToday(day) ? "bg-sky-100 text-sky-700 font-bold border-2 border-sky-400" : ""}
                ${holiday ? "bg-amber-50 text-amber-700 border border-amber-200" : "text-gray-700"}
              `}
                        >
                            {day}
                            {holiday && (
                                <>
                                    <div className="w-1.5 h-1.5 bg-amber-500 rounded-full mt-1"></div>
                                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-[10px] rounded opacity-0 group-hover:opacity-100 whitespace-nowrap z-50 transition">
                                        {holiday.name}
                                    </div>
                                </>
                            )}
                        </div>
                    );
                })}
            </div>

            <div className="mt-6 flex flex-wrap gap-4 text-xs font-medium">
                <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-sky-100 border border-sky-400 rounded"></div>
                    <span>Today</span>
                </div>
                <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-amber-50 border border-amber-200 rounded"></div>
                    <span>SL Holiday</span>
                </div>
            </div>
        </div>
    );
};

export default function DashboardHome() {
    const [localTime, setLocalTime] = useState(new Date());

    useEffect(() => {
        const timer = setInterval(() => setLocalTime(new Date()), 1000);
        return () => clearInterval(timer);
    }, []);

    return (
        <div className="space-y-8 animate-fade-in">
            {/* Header Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 text-white text-center">
                <div className="bg-gradient-to-br from-blue-600 to-blue-800 p-6 rounded-2xl shadow-lg relative overflow-hidden group">
                    <div className="relative z-10 flex flex-col items-center">
                        <Clock size={40} className="mb-2 opacity-80" />
                        <h4 className="text-sm font-medium opacity-80 uppercase">Sri Lanka Time</h4>
                        <span className="text-3xl font-bold font-mono tracking-tighter">
                            {localTime.toLocaleTimeString("en-GB", { hour12: false })}
                        </span>
                        <p className="text-xs mt-2 opacity-70 flex items-center gap-1">
                            <MapPin size={12} /> Colombo, Sri Lanka
                        </p>
                    </div>
                    <div className="absolute -right-8 -bottom-8 bg-white/10 w-32 h-32 rounded-full group-hover:scale-110 transition duration-500"></div>
                </div>

                <WorldClock city="London" timezone="Europe/London" />
                <WorldClock city="New York" timezone="America/New_York" />
                <WeatherWidget />
            </div>

            {/* Main Stats Area */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Calendar Section */}
                <div className="lg:col-span-2">
                    <CustomCalendar />
                </div>

                {/* Quick Actions / Welcome */}
                <div className="space-y-6">
                    <div className="bg-white p-6 rounded-2xl shadow-sm border border-sky-100 h-full">
                        <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                            <Globe className="text-sky-600" size={24} />
                            Welcome Back, Admin
                        </h3>
                        <p className="text-gray-600 text-sm leading-relaxed mb-6">
                            You are managing the TravelAura portal. Use the sidebar to manage destinations, accommodations, and events.
                        </p>

                        <div className="space-y-4">
                            <h4 className="text-xs font-bold text-gray-400 uppercase tracking-widest">Upcoming Holidays</h4>
                            {SL_HOLIDAYS_2026.filter(h => new Date(h.date) >= new Date()).slice(0, 3).map((h, i) => (
                                <div key={i} className="flex items-center justify-between p-3 bg-sky-50 rounded-xl border border-sky-100">
                                    <span className="text-sm font-semibold text-sky-800">{h.name}</span>
                                    <span className="text-[10px] font-bold text-sky-500 bg-white px-2 py-1 rounded-full shadow-sm">
                                        {new Date(h.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
